// ============================================================================
// /onboarding — Onboarding landing. Kanban (default) + List toggle, over the
// UNION of employee_onboarding + partner_onboarding. Type / Account / Priority
// filters. Terminal records (Cancelled / Complete) are hidden from the board.
// Spec §1, §2, §4.
// ============================================================================
import { canWriteOnboarding, isAccountManagerSeat } from '../../shared/role-gates';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutGrid, List as ListIcon, Archive as ArchiveIcon, Search } from 'lucide-react';
import { PageHeader, EmptyState, ErrorState, Skeleton } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { supabase } from '../../shared/supabase';
import { ExplainerButton } from '../../shared/explainer';
import { useOnboardingRecords } from './hooks/use-onboarding-data';
import { useMySubIds } from './hooks/use-my-sub-ids';
import { effectiveChecklist, computeProgress, computePriority } from './utils';
import OnboardingKanban from './onboarding-kanban';
import OnboardingList from './onboarding-list';
import OnboardingDetail from './onboarding-detail';
import { TYPE_CHIP_LABELS, type OnboardingRecord, type OnboardingStage, type OnboardingType } from './types';

// national_am has full AM authority (has_full_am_authority), same edit rights
// as hr/superuser across onboarding. Regular account_manager is read-only here.

type TypeFilter = OnboardingType | 'All';
type PriorityFilter = 'HIGH' | 'MEDIUM' | 'All';
type View = 'kanban' | 'list';

export default function OnboardingPage() {
  const { profile, employee } = useAuth();
  const canWrite = canWriteOnboarding(profile);
  const { records, ownerNames, loading, error, patchRecord, refetch } = useOnboardingRecords();
  const [params, setParams] = useSearchParams();
  // Ben, 2026-10-07 (item 818): an account manager opens on THEIR subs'
  // paperwork — a sub on a building they manage (the hub's own "My subs" rule)
  // or a hire assigned to them — and can switch to everyone. HR, the executives
  // and superusers see everyone starting, with the type filter.
  const me = employee?.employee_id ?? profile?.employee_id ?? null;
  const isAccountManager = isAccountManagerSeat(profile);
  const scopeParam = params.get('scope');
  const scope: 'mine' | 'all' =
    scopeParam === 'mine' || scopeParam === 'all' ? scopeParam : isAccountManager ? 'mine' : 'all';
  const mySubs = useMySubIds(isAccountManager ? me : null);

  const typeFilter = (params.get('type') as TypeFilter) || 'All';
  const accountFilter = params.get('account') || 'All';
  const priorityFilter = (params.get('priority') as PriorityFilter) || 'All';
  const [view, setView] = useState<View>('kanban');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // 2026-07-06 — search over card names + provider aliases ("Byron" finds
  // the DKK card). Debounced ~200ms; alias list fetched once (small table).
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [providerAliases, setProviderAliases] = useState<Map<string, string[]>>(
    new Map(),
  );
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 200);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: aliasErr } = await supabase
        .from('entity_aliases')
        .select('entity_id, alias')
        .eq('entity_type', 'service_provider');
      if (aliasErr || cancelled) return; // aliases are additive — soft-fail
      const m = new Map<string, string[]>();
      for (const r of (data ?? []) as { entity_id: string; alias: string }[]) {
        const list = m.get(r.entity_id) ?? [];
        list.push(r.alias);
        m.set(r.entity_id, list);
      }
      setProviderAliases(m);
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  // Archived (terminal) records can't sit on a stage board, so force List.
  const effectiveView: View = showArchived ? 'list' : view;

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (!value || value === 'All') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  // Default board shows active (non-terminal) records; the Archived toggle
  // flips to the terminal (Cancelled / Complete) set so they can be restored.
  const base = useMemo(
    () => records.filter((r) => (showArchived ? r.terminal : !r.terminal)),
    [records, showArchived],
  );

  const accountOptions = useMemo(
    () => [...new Set(base.map((r) => r.account).filter(Boolean) as string[])].sort(),
    [base],
  );

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return base.filter((r) => {
      if (isAccountManager && scope === 'mine') {
        const mine =
          (r.provider_id != null && mySubs.ids.has(r.provider_id)) ||
          (!!me && r.assigned_am === me);
        if (!mine) return false;
      }
      if (typeFilter !== 'All' && r.type !== typeFilter) return false;
      if (accountFilter !== 'All' && r.account !== accountFilter) return false;
      if (priorityFilter !== 'All') {
        const progress = computeProgress(effectiveChecklist(r.checklist), r.employmentTypeForChecklist);
        const p = computePriority(r.start_date, progress);
        if (p !== priorityFilter) return false;
      }
      if (q.length > 0) {
        const hay = [r.name, r.account, r.role]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        const aliasHit =
          r.provider_id != null &&
          (providerAliases.get(r.provider_id) ?? []).some((a) =>
            a.toLowerCase().includes(q),
          );
        if (!hay.includes(q) && !aliasHit) return false;
      }
      return true;
    });
  }, [base, typeFilter, accountFilter, priorityFilter, debouncedSearch, providerAliases, isAccountManager, scope, mySubs.ids, me]);

  // The People Desk links a record as ?open=source:id (item 818): open it once
  // the rows are here, then leave the person to it.
  const openParam = params.get('open');
  const [openedFromLink, setOpenedFromLink] = useState<string | null>(null);
  useEffect(() => {
    if (!openParam || openParam === openedFromLink || loading) return;
    setOpenedFromLink(openParam);
    if (records.some((r) => `${r.source}:${r.onboarding_id}` === openParam)) setSelectedKey(openParam);
  }, [openParam, openedFromLink, loading, records]);

  const selected =
    selectedKey != null
      ? records.find((r) => `${r.source}:${r.onboarding_id}` === selectedKey) ?? null
      : null;

  async function onStageChange(rec: OnboardingRecord, next: OnboardingStage) {
    await patchRecord(rec, { status: next }, { rawStatus: next, stage: next, terminal: false });
  }

  return (
    <div className="mx-auto max-w-[1400px] space-y-4 p-4 md:p-6">
      <PageHeader
        eyebrow="HR"
        title="Onboarding"
        meta={`${filtered.length} ${showArchived ? 'archived' : 'active'} · employee + partner tracks`}
        actions={
          <div className="flex items-center gap-2">
            {isAccountManager && (
              <div
                role="tablist"
                aria-label="Whose paperwork"
                className="flex items-center gap-0.5 rounded-md border border-tcs-border bg-tcs-bg p-0.5 text-xs"
              >
                {(['mine', 'all'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="tab"
                    aria-selected={scope === s}
                    onClick={() => setParam('scope', s)}
                    className={`rounded px-3 py-1 ${scope === s ? 'bg-tcs-surface font-medium text-tcs-text' : 'text-tcs-text-secondary'}`}
                  >
                    {s === 'mine' ? 'My subs' : 'Everyone'}
                  </button>
                ))}
              </div>
            )}
            <ExplainerButton surfaceKey="board:partner-onboarding" />
          </div>
        }
      />

      {/* Search — names + provider aliases (owner / shorthand nicknames) */}
      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tcs-text-muted" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, account, or alias…"
          className="block w-full rounded-md border border-tcs-border bg-tcs-field py-2 pl-9 pr-3 text-sm text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-border-input focus:outline-none"
        />
      </div>

      {/* Filters + view toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          label="Type"
          value={typeFilter}
          onChange={(v) => setParam('type', v)}
          options={[
            { value: 'All', label: 'All' },
            { value: 'W2', label: TYPE_CHIP_LABELS.W2 },
            // 2026-06-02 (Antonela H3): Partner folded into Subcontractor.
            { value: 'Subcontractor', label: TYPE_CHIP_LABELS.Subcontractor },
          ]}
        />
        <select
          value={accountFilter}
          onChange={(e) => setParam('account', e.target.value)}
          className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-xs"
        >
          <option value="All">All accounts</option>
          {accountOptions.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <Segmented
          label="Priority"
          value={priorityFilter}
          onChange={(v) => setParam('priority', v)}
          options={[
            { value: 'All', label: 'All' },
            { value: 'HIGH', label: 'High' },
            { value: 'MEDIUM', label: 'Medium' },
          ]}
        />
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          className={`ml-auto inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs ${
            showArchived
              ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
              : 'border-tcs-border text-tcs-text-secondary hover:bg-tcs-surface'
          }`}
        >
          <ArchiveIcon className="h-3.5 w-3.5" /> {showArchived ? 'Viewing archived' : 'Archived'}
        </button>
        <div className="flex items-center gap-1 rounded-md border border-tcs-border p-0.5">
          <button
            type="button"
            disabled={showArchived}
            onClick={() => setView('kanban')}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs disabled:opacity-40 ${
              effectiveView === 'kanban' ? 'bg-tcs-inverse text-tcs-inverse-ink' : 'text-tcs-text-secondary'
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Board
          </button>
          <button
            type="button"
            onClick={() => setView('list')}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs ${
              effectiveView === 'list' ? 'bg-tcs-inverse text-tcs-inverse-ink' : 'text-tcs-text-secondary'
            }`}
          >
            <ListIcon className="h-3.5 w-3.5" /> List
          </button>
        </div>
      </div>

      {error && <ErrorState title="Couldn't load onboarding records" body={error} />}

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No onboarding records match these filters" />
      ) : effectiveView === 'kanban' ? (
        <OnboardingKanban
          records={filtered}
          onSelect={(r) => setSelectedKey(`${r.source}:${r.onboarding_id}`)}
          onStageChange={canWrite ? onStageChange : undefined}
        />
      ) : (
        <OnboardingList
          records={filtered}
          canWrite={canWrite}
          onSelect={(r) => setSelectedKey(`${r.source}:${r.onboarding_id}`)}
          onPatch={patchRecord}
        />
      )}

      {selected && (
        <OnboardingDetail
          rec={selected}
          ownerNames={ownerNames}
          canWrite={canWrite}
          onPatch={patchRecord}
          onClose={() => setSelectedKey(null)}
          onRefetch={refetch}
        />
      )}
    </div>
  );
}

function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[11px] font-medium text-tcs-text-muted">{label}:</span>
      <div className="flex rounded-md border border-tcs-border p-0.5">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`rounded px-2 py-1 text-xs transition-colors ${
              value === o.value ? 'bg-tcs-inverse text-tcs-inverse-ink' : 'text-tcs-text-secondary hover:bg-tcs-surface'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
